import { useMemo } from 'react';
import { useQueryFetcher } from '@/lib/query/useQueryFetcher';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import {
    AssetsActiveResponse,
    BreakdownByCohortResponse,
    BreakdownBySizeResponse,
    PositionMetricsResponse,
    SegmentMetricsResponse,
} from '@/lib/types/hypertrackerTypes';
import { createUrl } from '@/lib/helpers/hypertrackerHelpers';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
    selectPerpsCurrentAsset,
    selectPerpsPositionAge,
    selectPositionExplorerFilters,
} from '@/state/hypertracker/hypertrackerSelector';

const getPositionAgeSuffix = (positionAge: string): string => {
    if (positionAge === 'all') return '';
    return `_${positionAge}`;
};

export const usePerpsData = () => {
    const isMemberAuthorized = useIsMemberAuthorized();
    const perpsCurrentAsset = useSelector((state: RootState) => selectPerpsCurrentAsset(state));
    const explorerFilters = useSelector((state: RootState) => selectPositionExplorerFilters(state));
    const perpsPositionAge = useSelector((state: RootState) => selectPerpsPositionAge(state));

    const ageSuffix = getPositionAgeSuffix(perpsPositionAge);
    const hasSuffix = ageSuffix !== '';

    const fileUrls = useMemo(
        () => ({
            assetsActive: `/assets/active.json`,
            breakdownBySize: `/assets/${perpsCurrentAsset}/coin-position-breakdown-by-size${ageSuffix}.json`,
            breakdownBySizeBase: `/assets/${perpsCurrentAsset}/coin-position-breakdown-by-size.json`,
            breakdownByCohort: `/assets/${perpsCurrentAsset}/coin-position-breakdown-by-cohort${ageSuffix}.json`,
            breakdownByCohortBase: `/assets/${perpsCurrentAsset}/coin-position-breakdown-by-cohort.json`,
            positionMetrics: `/assets/${perpsCurrentAsset}/position-metrics_v2.json`,
            segmentMetrics: `/assets/${perpsCurrentAsset}/segment-metrics_v2${ageSuffix}.json`,
            positions: `/assets/${perpsCurrentAsset}/positions${ageSuffix}.json`,
            positionsBase: `/assets/${perpsCurrentAsset}/positions.json`,
            liquidationHeatmap: `/assets/${perpsCurrentAsset}/liquidation-heatmap.json`,
        }),
        [perpsCurrentAsset, ageSuffix],
    );

    const assetsActiveQuery = useQueryFetcher<AssetsActiveResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.assetsActive),
    });

    const breakdownBySizeQuery = useQueryFetcher<BreakdownBySizeResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.breakdownBySize),
    });

    const breakdownBySizeFallbackQuery = useQueryFetcher<BreakdownBySizeResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.breakdownBySizeBase),
        enabled: hasSuffix && breakdownBySizeQuery.isError,
    });

    const breakdownByCohortQuery = useQueryFetcher<BreakdownByCohortResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.breakdownByCohort),
    });

    const breakdownByCohortFallbackQuery = useQueryFetcher<BreakdownByCohortResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.breakdownByCohortBase),
        enabled: hasSuffix && breakdownByCohortQuery.isError,
    });

    const positionMetricsQuery = useQueryFetcher<PositionMetricsResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.positionMetrics),
    });

    const segmentMetricsQuery = useQueryFetcher<SegmentMetricsResponse>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.segmentMetrics),
    });

    const positionsQuery = useQueryFetcher<any>({
        mode: 'auto',
        buildJsonUrl: () => createUrl(fileUrls.positions),
        isAuthorized: isMemberAuthorized,
        apiPath: HT_API_ROUTES.POSITIONS.ROOT,
        params: {
            ...explorerFilters,
            openedWithin: perpsPositionAge,
        } as any,
    });

    // JSON fallback for unauthenticated users when timeframe file doesn't exist yet
    const positionsFallbackQuery = useQueryFetcher<any>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.positionsBase),
        enabled: hasSuffix && !isMemberAuthorized && positionsQuery.isError,
    });

    const liquidationHeatmapQuery = useQueryFetcher<any>({
        mode: 'json',
        buildJsonUrl: () => createUrl(fileUrls.liquidationHeatmap),
    });

    const assetsActiveData = useMemo(() => {
        if (!assetsActiveQuery.data) return null;
        const { data } = assetsActiveQuery;
        const restructuredAssetsData = {
            coins: {},
            coins1HourAgo: {},
        };

        data.coins.data.forEach((item) => {
            restructuredAssetsData.coins[item[0]] = {
                positionCount: item[1],
                positionCountLong: item[2],
                totalPositionValue: item[3],
                totalPositionValueLong: item[4],
            };
        });

        data.coins1HourAgo.data.forEach((item) => {
            restructuredAssetsData.coins1HourAgo[item[0]] = {
                positionCount: item[1],
                positionCountLong: item[2],
                totalPositionValue: item[3],
                totalPositionValueLong: item[4],
            };
        });
        return restructuredAssetsData;
    }, [assetsActiveQuery.data]);

    const breakdownBySizeData = useMemo(() => {
        const data = breakdownBySizeQuery.data ?? breakdownBySizeFallbackQuery.data;
        if (!data) return null;
        return data.breakdownBySize;
    }, [breakdownBySizeQuery.data, breakdownBySizeFallbackQuery.data]);

    const breakdownByCohortData = useMemo(() => {
        const data = breakdownByCohortQuery.data ?? breakdownByCohortFallbackQuery.data;
        if (!data) return null;
        return data.cohortMetrics;
    }, [breakdownByCohortQuery.data, breakdownByCohortFallbackQuery.data]);

    const positionMetricsData = useMemo(() => {
        if (!positionMetricsQuery.data) return null;
        const { data } = positionMetricsQuery;
        return data.metrics?.data;
    }, [positionMetricsQuery.data]);

    const segmentMetricsData = useMemo(() => {
        if (!segmentMetricsQuery.data) return null;
        const { data } = segmentMetricsQuery;
        return data.data;
    }, [segmentMetricsQuery.data]);

    const liquidationHeatmapData = useMemo(() => {
        if (!liquidationHeatmapQuery.data) return null;
        const { data } = liquidationHeatmapQuery;
        return {
            [data.coin]: data.heatmap,
        };
    }, [liquidationHeatmapQuery.data]);

    const positionsData = useMemo(() => {
        const responseData = positionsQuery.data ?? positionsFallbackQuery.data;
        if (!responseData) return null;

        if (isMemberAuthorized) {
            return {
                positions: responseData.items,
                totalCount: responseData.totalCount,
            };
        }
        return {
            positions: responseData.positions?.concat(
                Array(5)
                    .fill(responseData.positions[0])
                    .map((item, index) => ({ ...item, id: item.id + index })),
            ),
            totalCount: 0,
        };
    }, [isMemberAuthorized, positionsQuery.data, positionsFallbackQuery.data]);

    return {
        queries: {
            positionsQuery,
            assetsActiveQuery,
            breakdownBySizeQuery,
            breakdownByCohortQuery,
            positionMetricsQuery,
            segmentMetricsQuery,
            liquidationHeatmapQuery,
        },
        data: {
            positionsData,
            assetsActiveData,
            breakdownBySizeData,
            breakdownByCohortData,
            positionMetricsData,
            segmentMetricsData,
            liquidationHeatmapData,
        },
    };
};
